/* ===================================================================
   XSREZ - same-team "You may also like"          26 Sep 2026

   WHAT WAS WRONG. The second recommendations rung on a product page
   runs Shopify's `related` intent, which scores on collection
   co-membership and product type. A Dallas Cowboys lapel pin sits in
   `lapel-pins` and `gifts-25-to-75` and is typed `Lapel Pin`, so the
   algorithm served Philadelphia Eagles, LA Chargers, New Orleans
   Saints and Jacksonville Jaguars pins - correct by type, useless to
   a Cowboys fan. Licensed sports is the one category where a rival
   team's crest is a guaranteed non-sale, and the "Completes the look"
   rung directly above was already serving four Cowboys pieces, so one
   rung was undoing the other.

   WHY IT IS FIXED IN JAVASCRIPT AND NOT IN LIQUID. The team lives in
   a TAG ("Dallas Cowboys"), not a metafield and not a collection.
   Liquid cannot query products by tag from a product page, and
   /recommendations/products.json takes no tag filter. What does work
   is the storefront facet the Team filter already uses:
   /collections/all?filter.p.tag=<Team>. This file fetches that and
   swaps the cards in.

   THE DURABLE FIX IS DATA, NOT CODE. Per-team collections - which
   NCAA already has and NFL, NBA, MLB and NHL do not - would make the
   native `related` intent produce this result on its own, and would
   also give each team an indexable landing page. Until those exist,
   this file stands in. See claude/xsrez-first-visit-conversion-audit.md.

   BEHAVIOUR
     - Product pages only.
     - No team tag (Dango, GRIP6, classic cufflinks and so on)? Do
       nothing. Related-by-type is the right answer for those, and
       this file must not make them worse.
     - Otherwise show same-team products, minus this one and minus
       whatever "Completes the look" is already showing above, and
       retitle the rung "More <Team>".
     - Fewer than two left? Hide the rung. An empty shelf beats a
       shelf of other teams' crests.
     - Anything unexpected - no <ul>, a failed fetch, a timeout - and
       the theme's own output is left exactly as it was.

   NON_TEAM mirrors the value list in
   snippets/team-filter-visibility.liquid. Keep the two in step: that
   snippet hides these from the Team facet, this one must not mistake
   one of them for a team name. Both carry the same scaling caveat -
   it is a by-name list, and the real answer is a custom.team
   metafield. See claude/xsrez-tee-luv-and-brands-axis.md.
   =================================================================== */

(function () {
  'use strict';

  var NON_TEAM = [
    'classic', 'sale', 'NCAA', 'dc-comics', 'dc', 'coca-cola',
    'women-underwear', "Rock 'Em Socks", 'Shopify Collective',
    'Cufflinks.com', 'Knockaround', 'Rockwell Time', 'Tee Luv',
    'VG Rings', 'RTZN', 'GRIP6', 'Dango Products', 'Comrad',
    'No Cold Feet', 'Trovvve', 'ModSoles', 'Pins and Aces',
    'ID Stronghold', 'BLACKOUT BARBELL', 'boldouterwear'
  ].map(function (v) { return v.toLowerCase(); });

  var MAX_CARDS = 8;
  var MIN_CARDS = 2;
  var POLL_MS = 250;
  var RUNG_TIMEOUT_MS = 8000;
  var COMPLEMENTS_TIMEOUT_MS = 4000;

  function handleFromPath(path) {
    var m = String(path || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function isTeam(tag) {
    return NON_TEAM.indexOf(String(tag).toLowerCase()) === -1;
  }

  // Poll rather than MutationObserver: the rung is hydrated by the
  // theme's own custom element, and we only need to know when it has
  // finished, not what changed.
  function waitFor(test, timeoutMs) {
    return new Promise(function (resolve) {
      var deadline = Date.now() + timeoutMs;
      (function tick() {
        var value = test();
        if (value) return resolve(value);
        if (Date.now() > deadline) return resolve(null);
        setTimeout(tick, POLL_MS);
      })();
    });
  }

  function handlesIn(root) {
    if (!root) return [];
    var out = [];
    // The Collective attribution blob, when present, lists the handles
    // outright and is in the DOM before the cards render.
    var blob = root.querySelector(
      '[data-shopify-collective-product-attribution]'
    );
    if (blob) {
      try {
        JSON.parse(blob.textContent).forEach(function (row) {
          if (row && row.handle) out.push(row.handle);
        });
      } catch (e) {}
    }
    root.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var h = handleFromPath(a.getAttribute('href'));
      if (h) out.push(h);
    });
    return out;
  }

  async function run() {
    var currentHandle = handleFromPath(window.location.pathname);
    if (!currentHandle) return;

    var rung = document.querySelector(
      'product-recommendations[data-intent="related"]'
    );
    if (!rung || rung.dataset.xsTeamRung) return;
    rung.dataset.xsTeamRung = 'pending';

    var product;
    try {
      var res = await fetch('/products/' + currentHandle + '.js', {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return;
      product = await res.json();
    } catch (e) {
      return;
    }

    var team = (product.tags || []).filter(isTeam)[0];
    if (!team) {
      // Not a team product. Shopify's related-by-type is correct here.
      rung.dataset.xsTeamRung = 'skipped-no-team';
      return;
    }

    // Same-team products, via the facet the Team filter already uses.
    var cards;
    try {
      var html = await fetch(
        '/collections/all?filter.p.tag=' + encodeURIComponent(team)
      ).then(function (r) { return r.ok ? r.text() : null; });
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      cards = Array.prototype.slice.call(
        doc.querySelectorAll('ul.product-grid > li')
      );
    } catch (e) {
      return;
    }
    if (!cards.length) return;

    // Do not repeat what "Completes the look" is already showing.
    var complements = document.querySelector(
      'product-recommendations[data-intent="complementary"]'
    );
    var exclude = [currentHandle];
    if (complements) {
      await waitFor(function () {
        return handlesIn(complements).length ? true : null;
      }, COMPLEMENTS_TIMEOUT_MS);
      exclude = exclude.concat(handlesIn(complements));
    }

    var seen = {};
    var keep = cards.filter(function (li) {
      var a = li.querySelector('a[href*="/products/"]');
      var h = a && handleFromPath(a.getAttribute('href'));
      if (!h || seen[h] || exclude.indexOf(h) !== -1) return false;
      seen[h] = true;
      return true;
    }).slice(0, MAX_CARDS);

    if (keep.length < MIN_CARDS) {
      rung.style.display = 'none';
      rung.dataset.xsTeamRung = 'hidden-too-few';
      return;
    }

    // Wait for the theme to finish rendering its own cards, then reuse
    // the list element it built so the rung keeps its grid, spacing and
    // column count. If it never renders one, leave the page alone.
    var list = await waitFor(function () {
      var li = rung.querySelector('li');
      return li ? li.parentElement : null;
    }, RUNG_TIMEOUT_MS);
    if (!list) {
      rung.dataset.xsTeamRung = 'skipped-no-list';
      return;
    }

    var itemClass = list.firstElementChild
      ? list.firstElementChild.className
      : '';

    var frag = document.createDocumentFragment();
    keep.forEach(function (li, i) {
      var node = li.cloneNode(true);
      if (itemClass) {
        node.className = itemClass.replace(/product-grid__item--\d+/,
          'product-grid__item--' + i);
      }
      frag.appendChild(node);
    });
    list.replaceChildren(frag);

    var heading = rung.querySelector('h1, h2, h3, h4');
    if (heading) heading.textContent = 'More ' + team;

    rung.dataset.xsTeamRung = 'applied';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();